import { useState } from 'react';
import { Loader2, RefreshCw, CheckCircle2, AlertTriangle, MinusCircle, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const STATUS_CONFIG = {
  live:           { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50', label: 'Live' },
  connected:      { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50', label: 'Connected' },
  recent:         { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50', label: 'Recent' },
  manual:         { icon: MinusCircle,  color: 'text-blue-600',  bg: 'bg-blue-50',  label: 'Manual' },
  stale:          { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50', label: 'Stale' },
  disconnected:   { icon: XCircle,      color: 'text-red-600',   bg: 'bg-red-50',   label: 'Disconnected' },
  missing:        { icon: XCircle,      color: 'text-red-600',   bg: 'bg-red-50',   label: 'Missing' },
  none:           { icon: XCircle,      color: 'text-gray-400',  bg: 'bg-gray-50',  label: 'None' },
  not_applicable: { icon: MinusCircle,  color: 'text-gray-300',  bg: '',             label: 'N/A' },
};

function StatusCell({ source }) {
  const status = source?.status || 'missing';
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.missing;
  const Icon = config.icon;

  const tooltip = [
    status,
    source?.account_count != null ? `${source.account_count} accounts` : null,
    source?.last_sync ? `last: ${new Date(source.last_sync).toLocaleDateString()}` : null,
    source?.count != null ? `${source.count} statements` : null,
    source?.latest ? `latest: ${source.latest}` : null,
  ].filter(Boolean).join(', ');

  return (
    <td className={`px-2 py-1.5 text-center ${config.bg}`} title={tooltip}>
      <div className="flex items-center justify-center gap-1">
        <Icon className={`h-3.5 w-3.5 ${config.color}`} />
        <span className={`text-xs ${config.color}`}>{config.label}</span>
      </div>
    </td>
  );
}

// Group entities by jurisdiction for display
function groupEntities(entities) {
  const groups = { France: [], UK: [], Personal: [] };
  for (const e of entities) {
    if (e.country === 'France') groups.France.push(e);
    else if (e.country === 'UK') groups.UK.push(e);
    else groups.Personal.push(e);
  }
  return groups;
}

export function CoverageMatrixPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.coverageMatrix();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const groups = result ? groupEntities(result.entities || []) : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Data Source Coverage Matrix</CardTitle>
        <p className="text-xs text-muted-foreground">
          Per-entity data source status. Rows = entities, columns = bank feed, Xero, Pennylane, statements.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button onClick={run} disabled={loading} size="sm" variant="outline">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>

        {result && (
          <div className="space-y-4">
            <div className="flex gap-3 text-sm">
              <span>Entities: <strong>{result.entity_count}</strong></span>
              <span className="text-xs text-muted-foreground">
                Generated: {new Date(result.generated_at).toLocaleString()}
              </span>
            </div>

            {Object.entries(groups).map(([groupLabel, entities]) => {
              if (entities.length === 0) return null;
              return (
                <div key={groupLabel}>
                  <h3 className="text-sm font-semibold mb-1 text-muted-foreground">{groupLabel}</h3>
                  <div className="overflow-x-auto border rounded">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left px-2 py-1.5 font-medium min-w-[180px]">Entity</th>
                          <th className="text-left px-2 py-1.5 font-medium">Role</th>
                          <th className="text-center px-2 py-1.5 font-medium">Bank Feed</th>
                          <th className="text-center px-2 py-1.5 font-medium">Xero</th>
                          <th className="text-center px-2 py-1.5 font-medium">Pennylane</th>
                          <th className="text-center px-2 py-1.5 font-medium">Statements</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entities.map((e) => (
                          <tr key={e.entity_id} className="border-b last:border-0 hover:bg-muted/20">
                            <td className="px-2 py-1.5">
                              <div className="flex items-center gap-1.5">
                                <span className="font-medium">{e.entity_name}</span>
                                <Badge variant="outline" className="text-[10px] px-1 py-0">{e.entity_id}</Badge>
                              </div>
                            </td>
                            <td className="px-2 py-1.5 text-muted-foreground">{e.group_role}</td>
                            <StatusCell source={e.sources?.bank_feed} />
                            <StatusCell source={e.sources?.xero} />
                            <StatusCell source={e.sources?.pennylane} />
                            <StatusCell source={e.sources?.statements} />
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
